import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Autocomplete, Chip, TextField } from '@mui/material';

import { useBrowseProducts } from '../../hooks';
import type { ProductSummary } from '../../model/types';

export interface PublicationRuleProductCriteriaProps {
  /** Full product objects (not just ids) so already-picked products render with a real name, not a raw id. */
  value: ProductSummary[];
  onChange: (products: ProductSummary[]) => void;
  disabled?: boolean;
}

/**
 * MANUAL_PRODUCT rule criteria — a product search picker reusing `useBrowseProducts` (the same
 * master-catalog browse endpoint `ProductsPage` itself calls), matching this app's existing
 * convention of firing a query per keystroke (no debounce hook exists anywhere in this codebase —
 * TanStack Query's own caching absorbs the repeat calls).
 */
export function PublicationRuleProductCriteria({
  value,
  onChange,
  disabled,
}: PublicationRuleProductCriteriaProps) {
  const { t } = useTranslation('catalog');
  const [inputValue, setInputValue] = useState('');
  const browseQuery = useBrowseProducts(
    { query: inputValue || undefined, size: 20 },
    { enabled: !disabled },
  );
  const options = browseQuery.data?.content ?? [];
  // Always keep already-selected products selectable/visible even if they fall out of the current
  // search result page (mirrors the promo workspace's per-id product-ref backfill precedent).
  const mergedOptions = [...value, ...options.filter((o) => !value.some((v) => v.id === o.id))];

  return (
    <Autocomplete
      multiple
      disabled={disabled}
      loading={browseQuery.isLoading}
      options={mergedOptions}
      value={value}
      filterOptions={(opts) => opts}
      inputValue={inputValue}
      onInputChange={(_e, next) => setInputValue(next)}
      getOptionLabel={(option) => `${option.name}${option.code ? ` (${option.code})` : ''}`}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      onChange={(_e, next) => onChange(next)}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return <Chip key={key} size="small" label={option.name} {...tagProps} />;
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={t('publicationRulesPanel.criteriaForm.productPickerLabel')}
          placeholder={
            value.length === 0 ? t('publicationRulesPanel.criteriaForm.productPickerPlaceholder') : undefined
          }
        />
      )}
    />
  );
}
